import { decodeDxf, formatAccessories, frontFromDxf, frontItems, getLang, LANGS, LIB, LIB_ORDER, parseAccessories, parseDxf, partText, type Front, type Part } from '@enercad/core';
import { itemsToSvg } from './editor/render';
import { api, getLibraryScope, setLibraryScope, type ImportResult, type User } from './api';
import { invalidateParts } from './parts';
import { autoOrient, glbSize, modelToGlb, type ModelOrient } from './model3d';
import { esc, openModal, qs, toast } from './ui';
import { t as $t } from '@enercad/core';


/* Parts database page: search, import/export Excel/CSV, edit (admins). */

const TYPE_OPTS = LIB_ORDER.filter((k) => !['pot', 'brk', 'text'].includes(LIB[k].role));
const num = (v: number | undefined) => (v == null ? '' : String(v).replace('.', ','));

export function partsView(root: HTMLElement, me: User, topbar: string, bindTop: () => void) {
  const admin = me.role === 'admin';
  root.innerHTML = `<div class="shell">${topbar}<main class="list" style="max-width:1200px">
    <div class="list-head"><h1>${$t("Parça veritabanı")}</h1>
      ${me.platform_admin ? `<span class="scope"><button class="btn${getLibraryScope() === 'tenant' ? ' on' : ''}" data-scope="tenant">${$t("Firmam")}</button><button class="btn${getLibraryScope() === 'shared' ? ' on' : ''}" data-scope="shared">${$t("Ortak kütüphane")}</button></span>` : ''}
      <a class="btn" href="/api/parts/export.xlsx" download>${$t("Excel olarak indir")}</a><a class="btn" href="/api/parts/export.bmecat.xml${getLibraryScope() === 'shared' ? '?scope=shared' : ''}" download title="${$t('Firma parçaları BMEcat 2005 kataloğu olarak: tanımlar tüm dillerde, fiyat, bağlantı, aksesuarlar; sembol tipi, kontaklar, ölçüler ve kablo bilgisi özellik olarak')}">${$t('BMEcat olarak indir')}</a>
      <button class="btn" id="guide" title="${$t('Siemens, Schneider, ABB, Phoenix Contact, Weidmüller ve WAGO verilerini nereden alacağınız')}">${$t('Üretici verisi')}</button>
      ${admin ? `<button class="btn" id="imp">${$t('İçe aktar (Excel / CSV / XML / ZIP)')}</button><button class="btn" id="fimp">${$t("Ön görünüşleri içe aktar…")}</button><button class="btn pri" id="add">${$t("+ Parça")}</button>` : ''}</div>
    <p class="note" style="margin:-6px 0 14px">${$t("Projelerde seçilen parçalar malzeme listesine, kontak kapasiteleri kontrol raporuna, ölçüler pano yerleşimine gider.{0}", admin ? '' : $t(" Parça ekleme ve içe aktarma yöneticiye açıktır."))}</p>
    <div class="parts-tools">
      <input class="search" id="q" placeholder="${$t("Parça no, tanım ya da üretici ara…")}" autocomplete="off">
      <select id="type" aria-label="${$t("Sembol tipi")}"><option value="">${$t("Tüm tipler")}</option>${TYPE_OPTS.map((k) => `<option value="${k}">${esc(LIB[k].name)}</option>`).join('')}</select>
      <span class="note" id="count" style="margin:0"></span>
    </div>
    <div class="tblw"><table class="t"><thead><tr><th>${$t("Parça no")}</th><th>${$t("Tanım")}</th><th>${$t("Üretici")}</th><th>${$t("Sembol tipleri")}</th><th>${$t("NO")}</th><th>${$t("NC")}</th><th>${$t("Kutup")}</th><th>${$t("Kanal")}</th><th>${$t("G × Y × D mm")}</th><th>${$t("Ön görünüş")}</th>${admin ? '<th></th>' : ''}</tr></thead><tbody id="rows"></tbody></table></div>
    <input type="file" id="file" accept=".xlsx,.csv,.txt,.xml,.zip" hidden>
    <input type="file" id="ffile" accept=".dxf,.png,.jpg,.jpeg" multiple hidden>
  </main></div>`;
  bindTop();
  const q = qs<HTMLInputElement>(root, '#q'), type = qs<HTMLSelectElement>(root, '#type');
  let current: Part[] = [];
  let timer = 0;

  const load = async () => {
    try {
      const r = await api.parts(q.value.trim(), type.value, 500);
      current = r.parts;
      qs(root, '#count').textContent = r.total > r.parts.length ? $t("{0} / {1} parça gösteriliyor", r.parts.length, r.total) : $t("{0} parça", r.total);
      qs(root, '#rows').innerHTML =
        r.parts
          .map(
            (p, i) => `<tr><td class="m">${esc(p.pn)}${p.shared ? ` <span class="pill ok" title="${$t("Platformun ortak kütüphanesinden; firmanıza kopyalayıp değiştirebilirsiniz")}">${$t("ortak")}</span>` : ''}</td><td>${esc(partText(p))}</td><td>${esc(p.maker)}</td>
              <td>${p.t.length ? p.t.map((t) => esc(LIB[t]?.name ?? t)).join(', ') : `<span class="note">${$t("tümü")}</span>`}</td>
              <td class="m">${p.no ?? ''}</td><td class="m">${p.nc ?? ''}</td><td class="m">${p.poles ?? ''}</td>
              <td class="m">${p.ch ?? ''}</td>
              <td class="m">${p.w != null ? `${num(p.w)} × ${num(p.h)} × ${num(p.dp)}` : ''}${p.kg != null ? ` · ${num(p.kg)} kg` : ''}</td>
              <td>${p.front ? `<span class="pill ok">${p.front === 'vec' ? $t("çizim") : 'resim'}</span>` : ''}</td>
              ${admin ? `<td style="white-space:nowrap">${p.shared ? `<button class="btn" data-edit="${i}" title="${$t("Firmanıza özel bir kopya oluşturur")}">${$t("Kopyala ve düzenle")}</button>` : `<button class="btn" data-edit="${i}">${$t("Düzenle")}</button> <button class="btn danger" data-del="${i}">${$t("Sil")}</button>`}</td>` : ''}</tr>`,
          )
          .join('') || `<tr><td colspan="9"><div class="empty">${$t("Eşleşen parça yok.")}</div></td></tr>`;
    } catch (e) {
      toast((e as Error).message);
    }
  };
  q.oninput = () => {
    clearTimeout(timer);
    timer = window.setTimeout(load, 200);
  };
  type.onchange = load;

  /** Uploads one front-view file (DXF → vector at 1:1, PNG/JPEG → picture) for a part. */
  const uploadFront = async (pn: string, file: File, setSize: boolean) => {
    if (/\.dxf$/i.test(file.name)) {
      const fv = frontFromDxf(parseDxf(decodeDxf(new Uint8Array(await file.arrayBuffer()))));
      return api.putFront(pn, fv, setSize);
    }
    return api.putFront(pn, file);
  };
  const showFront = (dlg: HTMLElement, f: Front | undefined, pn = '') => {
    const box = dlg.querySelector('#fprev');
    if (!box) return;
    if (!f) return void (box.innerHTML = `<span class="note">${$t("Ön görünüş yok")}</span>`);
    const W = 150, H = 150, s = Math.min(W / f.w, H / f.h);
    box.innerHTML = `<svg viewBox="0 0 ${f.w * s} ${f.h * s}" width="${f.w * s}" height="${f.h * s}">${itemsToSvg(frontItems(f, pn, 0, 0, f.w * s, f.h * s))}</svg>
      <span class="note">${f.kind === 'vec' ? $t("çizim · {0} × {1} mm", f.w, f.h) : $t("resim · {0} × {1} px", f.w, f.h)}</span>`;
  };

  const editDialog = (p?: Part) => {
    const v = p ?? { pn: '', d: '', t: [], maker: '' };
    const f = (id: string, label: string, val: unknown, extra = '') =>
      `<label class="fld"><span>${label}</span><input id="${id}" value="${esc(val ?? '')}" ${extra}></label>`;
    const close = openModal(
      `<h2>${p ? (p.shared ? $t("Ortak parçayı firmanıza kopyala") : $t("Parçayı düzenle")) : $t("Yeni parça")}</h2>
      ${p?.shared ? `<p>${$t("Kaydettiğinizde bu parçanın firmanıza özel bir kopyası oluşur; firmanızda ortak kütüphanedekinin yerine o kullanılır. Ortak kütüphane değişmez.")}</p>` : ''}
      ${getLibraryScope() === 'shared' ? `<p class="note">${$t("<b>Ortak kütüphaneyi</b> düzenliyorsunuz; değişiklik tüm firmalara yansır.")}</p>` : ''}
      ${f('pn', $t("Parça no"), v.pn, p ? 'readonly' : '')}${f('d', $t("Tanım"), v.d)}
      <details class="dlbox"${Object.keys(v.dl ?? {}).length || getLang() !== 'tr' ? ' open' : ''}><summary>${$t('Diğer dillerde tanım')}</summary>
        <p class="note">${$t('Boş bırakılan dilde Türkçe tanım (katalog parçalarında hazır çevirisi) gösterilir. Raporlar, PDF ve Excel çıktıları seçilen dilde bu tanımı kullanır.')}</p>
        <div class="dlgrid">${LANGS.filter((l) => l.code !== 'tr').map((l) => `<label class="fld"><span>${esc(l.name)}</span><input data-dl="${l.code}" value="${esc(v.dl?.[l.code] ?? '')}" placeholder="${esc(partText({ d: v.d }, l.code) === v.d ? '' : partText({ d: v.d }, l.code))}" dir="auto"></label>`).join('')}</div>
      </details>
      ${f('maker', $t("Üretici"), v.maker)}
      <label class="fld"><span>${$t("Sembol tipleri (Ctrl ile çoklu seçim, boş = tümü)")}</span><select id="t" multiple size="6">${TYPE_OPTS.map((k) => `<option value="${k}"${v.t.includes(k) ? ' selected' : ''}>${esc(LIB[k].name)}</option>`).join('')}</select></label>
      <div class="row2">${f('no', 'NO kontak', v.no, 'inputmode="numeric"')}${f('nc', 'NC kontak', v.nc, 'inputmode="numeric"')}</div>
      <div class="row2">${f('poles', $t("Ana kutup"), v.poles, 'inputmode="numeric"')}${f('ch', $t("PLC kanal sayısı"), v.ch, 'inputmode="numeric"')}</div>
      <div class="row2">${f('w', $t("Genişlik mm"), num(v.w), 'inputmode="decimal"')}${f('h', $t("Yükseklik mm"), num(v.h), 'inputmode="decimal"')}</div>
      <div class="row2">${f('dp', $t("Derinlik mm"), num(v.dp), 'inputmode="decimal"')}${f('kg', $t('Ağırlık kg'), num(v.kg), 'inputmode="decimal"')}</div>
      <div class="row2">${f('price', $t('Birim fiyat'), num(v.price), 'inputmode="decimal"')}<label class="fld"><span>${$t('Para birimi')}</span><select id="cur">${['EUR', 'TRY', 'USD', 'GBP', 'CHF'].map((c) => `<option${(v.cur || 'EUR') === c ? ' selected' : ''}>${c}</option>`).join('')}</select></label></div>
      ${f('acc', $t('Aksesuarlar / alt parçalar'), formatAccessories(v.acc), `placeholder="${$t('ör. YKB-22, ETK-KT×2')}"`)}
      ${f('terms', $t('Yardımcı kontak uçları (fonksiyon şablonu)'), v.terms, `placeholder="${$t('ör. 13-14, 21-22, 53-54, 61-62 · değiştirici: 11-12/14')}" title="${$t('Şemadaki yardımcı kontaklar bu uçlarla numaralanır ve kontrol edilir: …3-…4 ve …7-…8 kapanan, …1-…2 ve …5-…6 açan')}"`)}
      ${f('url', $t('Üretici sayfası / veri sayfası'), v.url, 'placeholder="https://…" inputmode="url"')}
      ${f('vis', $t('Görsel kaynağı (ön görünüşü ve 3D modeli kullanılacak parça no)'), v.vis, `placeholder="${$t('ör. aynı gövdedeki bir şalter')}"`)}
      ${v.pn ? `<p class="note" style="margin:-4px 0 10px">${portalLinks(v)}</p>` : ''}
      <details class="dlbox"${v.cores || v.ct ? ' open' : ''}><summary>${$t('Kablo bilgileri')}</summary>
        <div class="row2">${f('ct', $t('Kablo tipi'), v.ct, 'placeholder="NYY-J, LiYCY"')}${f('cs', $t('Kesit mm²'), num(v.cs as any), 'inputmode="decimal"')}</div>
        ${f('cores', $t('Damarlar (virgülle, sırayla)'), v.cores, 'placeholder="BN,BK,GY,GNYE"')}
        <label class="chkline"><input type="checkbox" id="shield"${v.shield ? ' checked' : ''}> ${$t('Ekranlı kablo')}</label>
      </details>
      ${p ? `<h3 class="subh">${$t("Ön görünüş (pano yerleşimi)")}</h3>
      <div class="frontbox"><div id="fprev" class="fprev"><span class="note">${$t("Yükleniyor…")}</span></div>
      <div><p class="note" style="margin-top:0">${$t("Üreticinin 2D ön görünüş DXF'i (1:1, mm) ya da PNG/JPG resim. Pano yerleşiminde kutu yerine bu çizilir.")}</p>
      <label class="chkline"><input type="checkbox" id="fsize" checked> ${$t("DXF'ten genişlik ve yüksekliği al")}</label>
      <div class="btnrow"><button class="btn" data-m="fup">${$t("Dosya seç…")}</button><button class="btn danger" data-m="fdel">${$t("Kaldır")}</button></div>
      <input type="file" id="fone" accept=".dxf,.png,.jpg,.jpeg" hidden></div></div>
      <h3 class="subh">${$t('3D model (3D pano görünümü)')}</h3>
      <p class="note" style="margin-top:0">${$t('Üreticinin STEP (.step, .stp) ya da IGES modeli, veya .glb. STEP tarayıcınızda 3D ağa çevrilir (ilk seferde yaklaşık 8 MB dönüştürücü indirilir). 3D görünümde kutu yerine bu model çizilir.')}</p>
      <div class="btnrow"><span class="note" id="mstat">${$t('Yükleniyor…')}</span></div>
      <div class="btnrow"><button class="btn" data-mm="up">${$t('Model seç…')}</button><button class="btn" data-mm="rot" title="${$t('Modelin eksenlerini değiştir (genişlik / yükseklik / derinlik)')}">${$t('Döndür')}</button><button class="btn" data-mm="flip">${$t('Önü arkaya çevir')}</button><button class="btn danger" data-mm="del">${$t('Kaldır')}</button></div>
      <input type="file" id="mone" accept=".step,.stp,.igs,.iges,.glb" hidden>` : ''}
      <div class="err" id="perr"></div>
      <div class="acts"><button class="btn" data-m="close">${$t("Vazgeç")}</button><button class="btn pri" data-m="save">${$t("Kaydet")}</button></div>`,
      async (a, dlg) => {
        if (a === 'fup') return qs<HTMLInputElement>(dlg, '#fone').click();
        if (a === 'fdel' && p) {
          try {
            await api.deleteFront(p.pn);
            showFront(dlg, undefined);
            load();
          } catch (e) {
            qs(dlg, '#perr').textContent = (e as Error).message;
          }
          return;
        }
        if (a !== 'save') return;
        const val = (id: string) => qs<HTMLInputElement>(dlg, '#' + id).value.trim();
        const n = (id: string) => (val(id) === '' ? undefined : Number(val(id).replace(',', '.')));
        const part: Part = {
          pn: val('pn'), d: val('d'), maker: val('maker'),
          t: [...qs<HTMLSelectElement>(dlg, '#t').selectedOptions].map((o) => o.value),
          no: n('no'), nc: n('nc'), poles: n('poles'), ch: n('ch'), w: n('w'), h: n('h'), dp: n('dp'), kg: n('kg'),
        };
        if (val('price') !== '') part.price = n('price');
        part.cur = qs<HTMLSelectElement>(dlg, '#cur').value;
        if (val('acc')) part.acc = parseAccessories(val('acc'));
        if (val('terms')) part.terms = val('terms');
        if (val('url')) part.url = val('url');
        if (val('vis')) part.vis = val('vis');
        if (val('ct')) part.ct = val('ct');
        if (val('cores')) part.cores = val('cores');
        if (val('cs')) part.cs = val('cs').replace(',', '.');
        part.shield = qs<HTMLInputElement>(dlg, '#shield').checked;
        const dl: Record<string, string> = {};
        dlg.querySelectorAll<HTMLInputElement>('input[data-dl]').forEach((i) => i.value.trim() && (dl[i.dataset.dl!] = i.value.trim()));
        if (Object.keys(dl).length) part.dl = dl;
        if (!part.pn) return void (qs(dlg, '#perr').textContent = $t("Parça numarası gerekli."));
        if (['no', 'nc', 'poles', 'ch', 'w', 'h', 'dp', 'kg', 'price'].some((k) => Number.isNaN((part as any)[k])))
          return void (qs(dlg, '#perr').textContent = $t("Sayı alanlarına yalnızca sayı yazın."));
        try {
          await api.savePart(part);
          invalidateParts();
          close();
          toast($t("Parça kaydedildi"));
          load();
        } catch (e) {
          qs(dlg, '#perr').textContent = (e as Error).message;
        }
      },
    );
  };

  root.querySelector('#add')?.addEventListener('click', () => editDialog());
  root.querySelectorAll<HTMLElement>('[data-scope]').forEach((b) =>
    b.addEventListener('click', () => {
      setLibraryScope(b.dataset.scope as 'tenant' | 'shared');
      root.querySelectorAll('[data-scope]').forEach((x) => x.classList.toggle('on', x === b));
      load();
    }),
  );
  // wire the front-view controls of the part dialog after it opens
  root.addEventListener('click', async (e) => {
    const b = (e.target as HTMLElement).closest<HTMLElement>('[data-edit]');
    if (!b) return;
    const p = current[+b.dataset.edit!];
    await new Promise((r) => setTimeout(r));
    const dlg = document.querySelector<HTMLElement>('.modal .dlg');
    if (!dlg || !p) return;
    try {
      showFront(dlg, (await api.fronts([p.pn])).fronts[p.pn], p.pn);
    } catch {
      showFront(dlg, undefined);
    }
    void setupModel(dlg, p);
    const one = dlg.querySelector<HTMLInputElement>('#fone');
    if (one)
      one.onchange = async () => {
        const f = one.files?.[0];
        one.value = '';
        if (!f) return;
        try {
          await uploadFront(p.pn, f, qs<HTMLInputElement>(dlg, '#fsize').checked);
          invalidateParts();
          showFront(dlg, (await api.fronts([p.pn])).fronts[p.pn], p.pn);
          const lk = (await api.lookupParts([p.pn])).parts[0];
          if (lk?.w != null) {
            qs<HTMLInputElement>(dlg, '#w').value = num(lk.w);
            qs<HTMLInputElement>(dlg, '#h').value = num(lk.h);
          }
          toast($t("Ön görünüş kaydedildi"));
          load();
        } catch (x) {
          qs(dlg, '#perr').textContent = (x as Error).message;
        }
      };
  });

  // bulk: file name (without extension) = part number
  const ffile = qs<HTMLInputElement>(root, '#ffile');
  root.querySelector('#fimp')?.addEventListener('click', () => ffile.click());
  ffile.onchange = async () => {
    const files = [...(ffile.files ?? [])];
    ffile.value = '';
    if (!files.length) return;
    const ok: string[] = [], missing: string[] = [], failed: string[] = [];
    toast($t("{0} dosya işleniyor…", files.length));
    for (const f of files) {
      const pn = f.name.replace(/\.(dxf|png|jpe?g)$/i, '').trim();
      try {
        await uploadFront(pn, f, true);
        ok.push(pn);
      } catch (x) {
        if ((x as { status?: number }).status === 404) missing.push(pn);
        else failed.push(`${f.name}: ${(x as Error).message}`);
      }
    }
    invalidateParts();
    load();
    openModal(
      `<h2>${$t("Ön görünüşler içe aktarıldı")}</h2>
      <dl class="kv"><dt>${$t("Bağlanan")}</dt><dd>${ok.length}</dd><dt>${$t("Parçası olmayan")}</dt><dd>${missing.length}</dd><dt>${$t("Okunamayan")}</dt><dd>${failed.length}</dd></dl>
      ${missing.length ? `<p class="note" style="margin-top:10px">${$t("Dosya adı bir parça numarasıyla eşleşmedi. Önce parçayı ekleyin ya da dosyayı parça numarasıyla yeniden adlandırın:")}</p><ul class="imp-errors">${missing.slice(0, 100).map((m) => `<li>${esc(m)}</li>`).join('')}</ul>` : ''}
      ${failed.length ? `<ul class="imp-errors">${failed.slice(0, 50).map((m) => `<li>${esc(m)}</li>`).join('')}</ul>` : ''}
      <div class="acts"><button class="btn pri" data-m="close">${$t("Tamam")}</button></div>`,
      () => {},
    );
  };
  qs(root, '#rows').addEventListener('click', (e) => {
    const t = e.target as HTMLElement;
    const ed = t.closest<HTMLElement>('[data-edit]')?.dataset.edit;
    if (ed != null) return editDialog(current[+ed]);
    const del = t.closest<HTMLElement>('[data-del]')?.dataset.del;
    if (del == null) return;
    const p = current[+del];
    const close = openModal(
      `<h2>${$t("{0} silinsin mi?", esc(p.pn))}</h2><p>${$t("Parça veritabanından kaldırılır. Bu parçayı kullanan projelerde parça numarası kalır ama tanım ve kapasite bilgisi gelmez.")}</p>
      <div class="acts"><button class="btn" data-m="close">${$t("Vazgeç")}</button><button class="btn pri" data-m="ok">${$t("Parçayı sil")}</button></div>`,
      async (a) => {
        if (a !== 'ok') return;
        try {
          await api.deletePart(p.pn);
          invalidateParts();
          close();
          load();
        } catch (x) {
          toast((x as Error).message);
        }
      },
    );
  });

  root.querySelector('#guide')?.addEventListener('click', () => makerGuide(admin));
  const file = qs<HTMLInputElement>(root, '#file');
  root.querySelector('#imp')?.addEventListener('click', () => file.click());
  file.onchange = () => {
    const f = file.files?.[0];
    file.value = '';
    if (!f) return;
    const close = openModal(
      `<h2>${$t("Parçaları içe aktar")}</h2><p>${$t("<b>{0}</b> okunacak. İlk satır başlık olmalı. \"Parça no\", \"Tanım\", \"Üretici\", \"Sembol tipi\", \"NO\", \"NC\", \"Genişlik\" gibi sütunlar otomatik tanınır (İngilizce ve Almanca adlar da). Türkçe Excel'in CSV biçimi desteklenir.", esc(f.name))}</p>
      <label class="chkline"><input type="checkbox" id="rep"> ${$t("Mevcut parçaları sil, yalnızca bu dosyadakiler kalsın")}</label>
      <p class="note">${$t("İşaretlenmezse aynı parça numaraları güncellenir, yeniler eklenir.")}</p>
      <div class="acts"><button class="btn" data-m="close">${$t("Vazgeç")}</button><button class="btn pri" data-m="go">${$t("İçe aktar")}</button></div>`,
      async (a, dlg) => {
        if (a !== 'go') return;
        const replace = qs<HTMLInputElement>(dlg, '#rep').checked;
        close();
        toast($t("İçe aktarılıyor…"));
        try {
          const r = f.size > 15 * 1024 * 1024 || /\.zip$/i.test(f.name) ? await bigImport(f, replace) : await api.importParts(f, replace);
          invalidateParts();
          openModal(
            `<h2>${$t("İçe aktarma tamamlandı")}</h2>
            <dl class="kv"><dt>${$t("Eklenen")}</dt><dd>${r.inserted}</dd><dt>${$t("Güncellenen")}</dt><dd>${r.updated}</dd><dt>${$t("Atlanan (parça no boş)")}</dt><dd>${r.skipped}</dd>
            ${r.format ? `<dt>${$t('XML biçimi')}</dt><dd>${esc({ bmecat: 'BMEcat / ETIM', eplan: 'EPLAN', generic: $t('genel XML') }[r.format] ?? r.format)}</dd>` : ''}
            <dt>${$t("Tanınan sütunlar")}</dt><dd>${Object.values(r.columns).map(esc).join(', ')}</dd></dl>
            ${r.errors.length ? `<p style="margin-top:10px">${$t("Uyarılar:")}</p><ul class="imp-errors">${r.errors.map((x) => `<li>${$t("Satır {0}: {1}", x.row, esc(x.msg))}</li>`).join('')}</ul>` : ''}
            <div class="acts"><button class="btn pri" data-m="close">${$t("Tamam")}</button></div>`,
            () => {},
          );
          load();
        } catch (e) {
          openModal(`<h2>${$t("İçe aktarılamadı")}</h2><p>${esc((e as Error).message)}</p><div class="acts"><button class="btn pri" data-m="close">${$t("Tamam")}</button></div>`, () => {});
        }
      },
    );
  };
  load();
}

/** Links to the part's own page and to a web search for its data sheet on the maker's site. */
function portalLinks(p: Pick<Part, 'pn' | 'maker' | 'url'>): string {
  const q = (s: string) => `https://duckduckgo.com/?q=${encodeURIComponent(s)}`;
  const site = MAKER_SITES.find(([re]) => re.test(p.maker ?? ''))?.[1];
  const links = [
    ...(p.url && /^https?:\/\//i.test(p.url) ? [[p.url, $t('Üretici sayfası')]] : []),
    [q(`${p.pn} ${site ? 'site:' + site : p.maker ?? ''} datasheet`), $t('Veri sayfasını ara')],
    [q(`${p.pn} ${site ? 'site:' + site : p.maker ?? ''} BMEcat OR dxf OR step`), $t('CAD / BMEcat verisi ara')],
  ];
  return links.map(([href, label]) => `<a href="${esc(href)}" target="_blank" rel="noopener noreferrer">${esc(label)} ↗</a>`).join(' · ');
}
const MAKER_SITES: [RegExp, string][] = [
  [/siemens/i, 'siemens.com'], [/schneider|telemecanique/i, 'se.com'], [/\babb\b/i, 'abb.com'], [/phoenix/i, 'phoenixcontact.com'],
  [/weidm/i, 'weidmueller.com'], [/eaton|moeller/i, 'eaton.com'], [/legrand/i, 'legrand.com'], [/rittal/i, 'rittal.com'],
  [/wago/i, 'wago.com'], [/omron/i, 'omron.com'], [/finder/i, 'findernet.com'], [/woodward/i, 'woodward.com'], [/lapp/i, 'lappkabel.com'],
];

const AXIS = ['X', 'Y', 'Z'];
const PERMS: [number, number, number][] = [[0, 2, 1], [0, 1, 2], [1, 2, 0], [1, 0, 2], [2, 1, 0], [2, 0, 1]];

/** 3D model section of the part dialog: upload (STEP → glb in the browser), orientation, removal. */
async function setupModel(dlg: HTMLElement, p: Part) {
  const stat = dlg.querySelector<HTMLElement>('#mstat'), input = dlg.querySelector<HTMLInputElement>('#mone');
  if (!stat || !input) return;
  let orient: ModelOrient | null = null, has = false;
  const show = () => {
    const o = orient ?? { perm: [0, 2, 1] as [number, number, number] };
    stat.textContent = has
      ? $t('Model var · genişlik {0}, yükseklik {1}, derinlik {2}{3}', AXIS[o.perm[0]], AXIS[o.perm[1]], AXIS[o.perm[2]], o.flip ? ' · ' + $t('çevrilmiş') : '')
      : $t('Model yok');
    dlg.querySelectorAll<HTMLButtonElement>('[data-mm="rot"],[data-mm="flip"],[data-mm="del"]').forEach((b) => (b.disabled = !has));
  };
  try {
    const r = await api.partModels([p.pn]);
    has = p.pn in r.models;
    orient = r.models[p.pn] ?? null;
  } catch {
    has = false;
  }
  show();
  const saveOrient = async (o: ModelOrient) => {
    try {
      await api.setPartModelOrient(p.pn, o);
      orient = o;
      show();
    } catch (e) {
      toast((e as Error).message);
    }
  };
  dlg.addEventListener('click', async (e) => {
    const a = (e.target as HTMLElement).closest<HTMLElement>('[data-mm]')?.dataset.mm;
    if (!a) return;
    const o = orient ?? { perm: [0, 2, 1] as [number, number, number] };
    if (a === 'up') return input.click();
    if (a === 'rot') {
      const i = PERMS.findIndex((x) => x.join() === o.perm.join());
      return saveOrient({ ...o, perm: PERMS[(i + 1) % PERMS.length] });
    }
    if (a === 'flip') return saveOrient({ ...o, flip: !o.flip });
    if (a === 'del')
      try {
        await api.deletePartModel(p.pn);
        has = false;
        orient = null;
        show();
        toast($t('3D model kaldırıldı'));
      } catch (x) {
        toast((x as Error).message);
      }
  });
  input.onchange = async () => {
    const f = input.files?.[0];
    input.value = '';
    if (!f) return;
    stat.textContent = $t('Model dönüştürülüyor…');
    try {
      const glb = await modelToGlb(f);
      if (glb.size > 15 * 1024 * 1024) throw new Error($t('3D model en fazla 15 MB olabilir.'));
      const size = await glbSize(glb);
      const num = (id: string) => Number(dlg.querySelector<HTMLInputElement>('#' + id)?.value.replace(',', '.')) || undefined;
      const o = autoOrient(size, { w: num('w'), h: num('h'), dp: num('dp') });
      await api.savePartModel(p.pn, glb, o);
      has = true;
      orient = o;
      // parts without dimensions take them from the model
      (['w', 'h', 'dp'] as const).forEach((id, i) => {
        const el = dlg.querySelector<HTMLInputElement>('#' + id);
        if (el && !el.value.trim()) el.value = String(Math.round(size[o.perm[i]] * 10) / 10).replace('.', ',');
      });
      show();
      toast($t('3D model kaydedildi ({0} KB). Ölçüler boşsa modelden dolduruldu; kaydetmeyi unutmayın.', Math.round(glb.size / 1024)));
    } catch (e) {
      show();
      toast((e as Error).message);
    }
  };
}

/**
 * Large or zipped catalogues: upload in chunks, then the server imports in the
 * background; a progress window follows both steps.
 */
async function bigImport(f: File, replace: boolean): Promise<ImportResult> {
  const close = openModal(
    `<h2>${$t('Büyük katalog içe aktarılıyor')}</h2><p id="bstat">${$t('Yükleniyor…')}</p><div class="bbar"><div id="bfill"></div></div>
    <p class="note">${$t('Bu pencereyi kapatmayın. Yükleme bitince içe aktarma sunucuda sürer.')}</p>`,
    () => {},
  );
  const stat = document.querySelector<HTMLElement>('#bstat')!, fill = document.querySelector<HTMLElement>('#bfill')!;
  const mb = (n: number) => (n / 1024 / 1024).toFixed(1).replace('.', ',');
  let id = '';
  try {
    const s = await api.startUpload(f.name, f.size);
    id = s.id;
    for (let o = 0; o < f.size; o += s.chunk) {
      // a chunk may be retried a few times on a flaky connection
      for (let t = 0; ; t++) {
        try {
          await api.uploadChunk(id, o, f.slice(o, o + s.chunk));
          break;
        } catch (e) {
          if (t >= 3) throw e;
          await new Promise((r) => setTimeout(r, 1000 * (t + 1)));
        }
      }
      const done = Math.min(f.size, o + s.chunk);
      fill.style.width = `${(done / f.size) * 100}%`;
      stat.textContent = $t('Yükleniyor: {0} / {1} MB', mb(done), mb(f.size));
    }
    await api.startUploadImport(id, replace);
    for (;;) {
      await new Promise((r) => setTimeout(r, 1000));
      const st = await api.uploadStatus(id);
      stat.textContent = $t('İçe aktarılıyor: {0} parça işlendi', st.rows.toLocaleString());
      fill.classList.add('busy');
      if (st.state === 'error') throw new Error(st.error ?? $t('Dosya okunamadı.'));
      if (st.state === 'done' && st.result) return st.result;
    }
  } catch (e) {
    if (id) void api.cancelUpload(id).catch(() => {});
    throw e;
  } finally {
    close();
  }
}

/** Where to get each manufacturer's parts and CAD data, and how to bring it in. */
function makerGuide(admin: boolean) {
  const makers: [string, string, string, [string, string][]][] = [
    ['Siemens', $t('SiePortal → mySiePortal → Ürün verileri → Ana veri indirme (Stammdatendownload). Biçim olarak ECLASS/BMEcat 2005 ya da Excel seçin. Tüm katalog da seçilebilir; paket birkaç saat içinde hazırlanır. Giriş gerekir.'),
      $t('SiePortal CAx indirme: sipariş numaralarını seçip 2D DXF ve 3D STEP alın. Giriş gerekir.'),
      [['https://sieportal.siemens.com', 'SiePortal']]],
    ['Schneider Electric', $t('Ana veri (ETIM) Schneider Türkiye temsilciniz üzerinden, iş ortağı veri akışı ya da ürün kataloğu API erişimiyle alınır.'),
      $t('se.com ürün sayfasında "CAD" düğmesi: DXF ve STEP (TraceParts üzerinden, ücretsiz üyelik).'),
      [['https://www.se.com/tr/tr/', 'se.com']]],
    ['ABB', $t('ABB Library\'de "Artikelstammdaten" ya da fiyat listesi araması: BMEcat + ETIM 9/10, çoğunlukla ZIP. Doğrudan müşterilere açıktır.'),
      $t('ABB ürünleri için CADENAS PARTcommunity: STEP ve DXF (ücretsiz üyelik).'),
      [['https://library.abb.com', 'ABB Library'], ['https://abb-control-products.partcommunity.com', 'PARTcommunity']]],
    ['Phoenix Contact', $t('My Phoenix Contact hesabıyla veri sağlama sayfasından BMEcat ya da Excel (ETIM ve ECLASS sınıflı).'),
      $t('Ürün sayfalarında STEP ve DXF; EPLAN verisi için EPLAN dosya üreticisi.'),
      [['https://www.phoenixcontact.com/en-us/service-and-support/data-provisioning', 'Data provisioning']]],
    ['Weidmüller', $t('BMEcat ya da ETIM/ECLASS CSV, elektronik katalog sayfasındaki formla istenir ve size özel hazırlanır.'),
      $t('STEP ürün sayfasında serbestçe; DXF 3Dfindit / PARTcommunity üzerinden (ücretsiz üyelik).'),
      [['https://www.weidmuller.com/en/service/electronics_catalogue_in_bmecat_and_other_formats.jsp', 'BMEcat'], ['https://weidmueller.partcommunity.com', 'PARTcommunity']]],
    ['WAGO', $t('BMEcat, ETIM ve Excel verisi WAGO destek / satış ekibinden istenir.'),
      $t('CADENAS PARTcommunity: STEP ve DXF, birden çok ürün birlikte (ücretsiz üyelik).'),
      [['https://www.wago.com/global/support', 'WAGO'], ['https://wago.partcommunity.com', 'PARTcommunity']]],
  ];
  openModal(
    `<div class="guide"><h2>${$t('Üretici verisi nasıl alınır')}</h2>
    <p>${$t('Üreticilerin parça ve CAD verisini kendi hesabınızla indirip EnerCAD\'e yükleyin; veriler yalnızca firmanızın kütüphanesine girer. Üreticilerin kullanım koşulları bu verilerin başkalarıyla paylaşılmasına genellikle izin vermez.')}</p>
    <ol class="steps"><li>${$t('<b>Parça verisi</b> (BMEcat XML, ZIP ya da Excel): Parçalar → "İçe aktar". 2 GB\'a kadar dosya ve ZIP arşivi yüklenebilir; içindeki XML, CSV ya da Excel bulunup okunur.')}</li>
    <li>${$t('<b>2D ön görünüş</b> (DXF): Parçalar → "Ön görünüşleri içe aktar…" — dosya adı parça numarası olmalı (ör. 3RT2015-1BB41.dxf) ya da parçanın penceresinden tek tek.')}</li>
    <li>${$t('<b>3D model</b> (STEP): parçanın penceresi → "3D model" → "Model seç…".')}</li></ol>
    ${makers.map(([name, data, cad, links]) => `<details><summary><b>${name}</b></summary><p><b>${$t('Parça verisi')}:</b> ${data}</p><p><b>${$t('CAD verisi')}:</b> ${cad}</p><p>${links.map(([u, l]) => `<a href="${u}" target="_blank" rel="noopener noreferrer">${l} ↗</a>`).join(' · ')}</p></details>`).join('')}
    <p class="note">${$t('Bağlantılar ve menü adları üreticilerin sitelerine göre değişebilir.')}${admin ? '' : ' ' + $t('İçe aktarma firma yöneticisine açıktır.')}</p>
    <div class="acts"><button class="btn pri" data-m="close">${$t('Kapat')}</button></div></div>`,
    () => {},
  );
}
